// Criteria pass rate rollup, sorted (scorecard, criterion name) to
// approximate the author-defined scorecard order; true order-index
// sorting needs BE to surface criteria_order. Legacy "Funnel" id kept
// for layout-key stability.
import React, { useMemo, useContext } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { EntityClickContext } from '../helpers/dashboardContexts'
import { colorForPassRate, escapeHtml } from '../helpers/format'
import { ChartWidgetCard, axisTooltip, useEntityClickEvents } from './chartWidget'

export const CriteriaPassRateFunnelWidget = () => {
  const criteria = useSelector((state) => state.qaCopilot.data.nonCompliantCriteria)
  const loading = useSelector((state) => state.qaCopilot.loading.nonCompliantCriteria)
  const clickEntity = useContext(EntityClickContext)

  const points = useMemo(() => {
    if (!criteria || !Array.isArray(criteria)) {
      return []
    }
    return criteria
      .map((criterion) => {
        const failingPct = parseFloat(
          criterion.failing_percent ?? criterion.percent ?? criterion.value ?? 0
        )
        return {
          name: criterion.name || criterion.criteria_name || 'Unknown',
          value: Math.max(0, Math.min(100, 100 - failingPct)),
          scorecardName: criterion.scorecard_config_name || criterion.scorecard_name || '',
          criterion,
        }
      })
      .sort((rowA, rowB) => {
        if (rowA.scorecardName !== rowB.scorecardName) {
          return rowA.scorecardName.localeCompare(rowB.scorecardName)
        }
        return rowA.name.localeCompare(rowB.name)
      })
      .slice(0, 12)
  }, [criteria])

  const option = useMemo(() => {
    if (isEmpty(points)) {
      return {}
    }
    return {
      tooltip: axisTooltip((params) => {
        const point = params[0]
        const cta = clickEntity ? '<br/><em>Click to drill into criterion</em>' : ''
        return `${escapeHtml(point.name)}<br/>Pass rate: ${point.value}%${cta}`
      }),
      grid: { left: 200, right: 24, top: 8, bottom: 24 },
      xAxis: { type: 'value', min: 0, max: 100, name: '% pass', axisLabel: { fontSize: 10 } },
      yAxis: {
        type: 'category',
        data: points.map((point) => point.name),
        axisLabel: {
          fontSize: 11,
          formatter: (value) => (value.length > 28 ? `${value.slice(0, 26)}…` : value),
        },
      },
      series: [
        {
          type: 'bar',
          cursor: 'pointer',
          barWidth: '60%',
          data: points.map((point) => ({
            value: point.value,
            itemStyle: { color: colorForPassRate(point.value), borderRadius: [0, 3, 3, 0] },
            criterion: point.criterion,
          })),
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            formatter: ({ value }) => `${value}%`,
          },
        },
      ],
    }
  }, [points, clickEntity])

  const events = useEntityClickEvents('criterion', 'criterion')

  return (
    <ChartWidgetCard
      title="Criteria Pass Rate"
      info="Pass rate for each criterion, lowest first, so the criteria agents struggle with most rise to the top. Click one to drill in."
      hint="Click a bar to open the criterion drawer"
      empty={isEmpty(points)}
      emptyText="No failing criteria in this window."
      loading={loading}
      option={option}
      events={events}
      height="320px"
    />
  )
}
